import { Select, Table, TableColumnsType } from "antd";
import _ from "lodash";
import moment from "moment";
import { FC, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useSelector } from "react-redux";
import { getAttendanceByStudent } from "../../../api/statistics.api";
import useAxios from "../../../hooks/useAxios";
import config from "../../../lib/config";
import exportTable from "../../../lib/exportTable";
import { getCommonCodeLabel } from "../../../lib/store";
import { IAttendanceByStudent, IGetAttendanceByStudent } from "../../../models/statistics.model";
import { RootState } from "../../../store/rootReducer";
import DownloadButton from "../../DownloadButton";
import ErrorResponse from "../../ErrorResponse";
import Loader from "../../Loader";
import Pagination from "../../Pagination";

interface IProps {
  data: IAttendanceByStudent[]|undefined;
  total: number|undefined;
  pageNo: number;
  setPageNo: (pageNo: number) => void;
  srtDate: string;
  endDate: string;
  campusCodes: string[];
  attendStatus: string|undefined;
  setAttendStatus: (attendStatus: string) => void;
}

const ByStudent: FC<IProps> = ({
  data,
  total,
  pageNo,
  setPageNo,
  srtDate,
  endDate,
  campusCodes,
  attendStatus,
  setAttendStatus,
}) => {
  const { t, i18n } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // CL0001 = 출결 상태
  const CL0001 = commonCodes.find((item) => item.commonCode === 'CL0001')?.detailCode;  
  const { data: res, isLoading, error, execute } = useAxios<IGetAttendanceByStudent>();

  useEffect(() => {
    if (res?.data && !isLoading) {
      const _data: IAttendanceByStudent[] = _.cloneDeep(res.data);
      _data.forEach(item => {
        item.attendStatus = getCommonCodeLabel('CL0001', item.attendStatus, 'ko');
      });
      const currentDate = moment(new Date());
      exportTable(columns, _data, `${t('statistics.attendance.title1')}(${t('common.menu.student')})_(${currentDate.format('YYYYMMDD')})`, `${t('common.menu.student')}`, [12, 10, 20, 20, 20, 12, 10, 10, 20]);
    }
  }, [res, isLoading]);

  const onExportTable = () => {
    if (srtDate && endDate && campusCodes) {
      execute(getAttendanceByStudent({
        srtDate,
        endDate,
        campusCode: auth.hqYn === 'Y'
          ? campusCodes
          : (campusCodes.length ? campusCodes : auth.campus),
        pageNo: 1,
        perPage: 999999999,
        ...(attendStatus ? { attendStatus } : {}),
      }));
    }
  }

  const columns: TableColumnsType<IAttendanceByStudent> = [
    {
      title: t('common.menu.campus'),
      dataIndex: 'campusKorName',
      align: 'center',
    },
    {
      title: t('student.student.column5'),
      dataIndex: 'studentKorName',
      align: 'center',
    },
    {
      title: t('auth.login.label1'),
      dataIndex: 'webId',
      align: 'center',
    },
    {
      title: t('student.student.column64'),
      dataIndex: 'studentCode',
      align: 'center',
    },
    {
      title: t('statistics.attendance.1'),
      dataIndex: 'teacherKorName',
      align: 'center',
    },
    {
      title: t('common.menu.class'),
      dataIndex: 'className',
      align: 'center',
    },
    {
      title: t('statistics.attendance.column7'),
      dataIndex: 'timetableDate',
      align: 'center',
    },
    {
      title: t('statistics.attendance.column8'),
      dataIndex: 'srtTime',
      align: 'center',
    },
    {
      title: t('student.student.column4'),
      dataIndex: 'attendStatus',
      align: 'center',
      render: value => getCommonCodeLabel('CL0001', value, 'ko'),
    },
    {
      title: t('statistics.attendance.column9'),
      dataIndex: 'memo',
      align: 'center',
    },
  ];

  return (
    <>
      {isLoading && <Loader />}
      <ErrorResponse error={error} />

      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <div className="w-150p">
            <Select
              className="w-100"
              placeholder={t('statistics.attendance.2')}
              value={attendStatus}
              onChange={value => setAttendStatus(value)}
            >
              <Select.Option value="">{t('common.text.all')}</Select.Option>
              {
                CL0001?.map((item: any) => {
                  return (
                    <Select.Option
                      key={item.detailCode}
                      value={item.detailCode}
                    >
                      {i18n.language === 'ko' ? item.korName : item.engName}
                    </Select.Option>
                  )
                })
              }
            </Select>
          </div>
        </div>
      </div>
      <div className="comm-table__area">
        <Table
          columns={columns}
          dataSource={data}
          pagination={{
            current: pageNo,
            pageSize: config.pageSize,
          }}
        />
      </div>
      <div className="comm-bx__between">
        <DownloadButton onClick={onExportTable} />
        <div className="common-paging center">
          <Pagination
            count={total || 0}
            page={pageNo}
            setPage={setPageNo}
          />
        </div>
      </div>
    </>
  );
}

export default ByStudent;
